import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { getAuthInstance } from '../lib/firebase';
import type { SessionScope } from '@mbbe/config';
import { apiFetch } from '../lib/apiClient';
import { adminSession } from '../lib/session';
import { setActiveAccount } from '../lib/accountScope';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  role: 'admin' | 'superadmin' | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const SESSION_SUPERSEDED = 'SESSION_SUPERSEDED';

type AdminRole = 'admin' | 'superadmin';

const toAdminRole = (role: string | null): AdminRole | null =>
  role === 'admin' || role === 'superadmin' ? role : null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<AdminRole | null>(null);
  // Identity binding (replaces a boolean "sign-in handled" flag): the scope a
  // role request was started for, and the last role committed for the account.
  const loadStartedRef = React.useRef<SessionScope | null>(null);
  const committedRef = React.useRef<{ uid: string | null; role: AdminRole | null } | null>(null);

  const clearAccountState = () => {
    committedRef.current = null;
    setIsAdmin(false);
    setRole(null);
  };

  // A role change for the same account rotates the scope so data cached under
  // the previous access level is dropped and re-requested.
  const settleScope = (scope: SessionScope, nextRole: AdminRole | null): SessionScope => {
    const previous = committedRef.current;
    committedRef.current = { uid: scope.uid, role: nextRole };
    if (!previous || previous.uid !== scope.uid || previous.role === nextRole) return scope;
    const rotated = adminSession.begin(scope.uid);
    loadStartedRef.current = rotated;
    return rotated;
  };

  useEffect(() => {
    const auth = getAuthInstance();
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      const uid = nextUser?.uid ?? null;
      // Sign-out and account switches happen without a page reload: tell per-operator UI state
      // (unsaved drafts, #176) first. A no-op while the account is unchanged.
      setActiveAccount(uid);
      // The sign-in flow (or an earlier event) already owns this identity.
      if (uid !== null && adminSession.current.uid === uid && loadStartedRef.current === adminSession.current) return;

      const scope = adminSession.ensure(uid);
      setIsLoading(true);
      setUser(nextUser);
      clearAccountState();
      if (!nextUser) {
        setIsLoading(false);
        return;
      }

      loadStartedRef.current = scope;
      let owner = scope;
      try {
        const result = await apiFetch<{ role: string | null }>('/admin/me', {}, true);
        if (!scope.isCurrent()) return;
        const nextRole = toAdminRole(result.role);
        owner = settleScope(scope, nextRole);
        setRole(nextRole);
        setIsAdmin(nextRole !== null);
      } catch {
        if (!scope.isCurrent()) return;
        clearAccountState();
      }
      if (owner.isCurrent()) setIsLoading(false);
    });

    return () => {
      unsubscribe();
      adminSession.end();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const auth = getAuthInstance();

    // Wrap Firebase sign-in in its own try/catch
    let credential;
    try {
      credential = await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      console.error('[Auth] Firebase sign-in failed:', err);
      throw new Error('FIREBASE_AUTH_ERROR:' + (err.code || 'unknown'));
    }

    // Starting B's scope aborts A's requests and drops A's cached data
    // synchronously, before B's state is rendered.
    const scope = adminSession.begin(credential.user.uid);
    loadStartedRef.current = scope;
    setActiveAccount(credential.user.uid);
    clearAccountState();
    setUser(credential.user);

    const abandon = async () => {
      await firebaseSignOut(auth);
      // Another account may have signed in while the sign-out was in flight.
      if (adminSession.current.uid !== null && !scope.isCurrent()) return;
      adminSession.ensure(null);
      setActiveAccount(null);
      setUser(null);
      clearAccountState();
      setIsLoading(false);
    };

    // Check admin role inline
    try {
      const result = await apiFetch<{ role: string | null }>('/admin/me', {}, true);
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      const nextRole = toAdminRole(result.role);
      if (nextRole === null) {
        // Not an admin - sign out and throw
        await abandon();
        throw new Error('NOT_ADMIN');
      }
      const owner = settleScope(scope, nextRole);
      setIsAdmin(true);
      setRole(nextRole);
      if (owner.isCurrent()) setIsLoading(false);
    } catch (err) {
      // Our own control-flow errors are re-thrown unchanged
      if (err instanceof Error && (err.message === 'NOT_ADMIN' || err.message === SESSION_SUPERSEDED)) {
        throw err;
      }
      // A request cancelled by a newer session must not sign the newer account out.
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      // API call failed - sign out and throw
      console.error('[Auth] Backend /admin/me check failed:', err);
      await abandon();
      throw new Error('ADMIN_CHECK_FAILED');
    }
  };

  const signOut = async () => {
    const auth = getAuthInstance();
    const previous = adminSession.current;
    // Drop the account's requests and cached data before the network sign-out.
    adminSession.end();
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      if (adminSession.current.uid === null && previous.uid) adminSession.begin(previous.uid);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAdmin, role, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
